import { Reducer } from 'react';
import * as Action from './Events.actions';
import { withLoadable } from 'utils/hoc';
import { EventsActionTypes, EventsState } from './Event.interfaces';
import { IDetailedEvent } from '../../interfaces';

const initialState: EventsState = {
  loading: false,
  error: null,
  loggedIn: false,
  eventsList: [],
  detailedEvents: [],
  totalEvents: 0,
  hasMore: false,
  singleEvent: null,
  oneEventForEdit: null,
  queryParams: {},
  contributorInfo: null,
  favorites: [],
  userData: null,
  playerList: {
    list: [],
    account: [],
    index: 0,
  },
  filteredTags: [],
  duplicateEventId: null,
  phonetics: [],
  eventPrices: [],
  loadingPodcast: false,
  loadingTranscript: false,
  podcast: null,
  transcript: '',
  isProcessingEvent: false,
};

const reducer: Reducer<EventsState, EventsActionTypes> = (state = initialState, action) => {
  switch (action.type) {
    case Action.OPEN_PLAYER: {
      return {
        ...state,
        playerList: action.payload,
        loading: false,
      };
    }
    case Action.DUPLICATE_EVENT: {
      return {
        ...state,
        duplicateEventId: action.payload,
      };
    }
    case Action.CLEAR_DUPLICATE_EVENT: {
      return {
        ...state,
        duplicateEventId: null,
      };
    }
    case Action.CLEAR_DETAILED_EVENTS: {
      return {
        ...state,
        detailedEvents: [],
      };
    }
    case Action.CLEAR_ONE_EVENT: {
      return {
        ...state,
        oneEventForEdit: null,
        eventPrices: [],
        podcast: null,
        transcript: '',
      };
    }
    case Action.GET_EVENTS_SUCCESS: {
      return {
        ...state,
        eventsList: [...state.eventsList, ...action.payload.results],
        totalEvents: action.payload.count,
        hasMore: !!action.payload.next,
      };
    }
    case Action.GET_DETAILED_EVENTS_SUCCESS: {
      return {
        ...state,
        detailedEvents: [...state.detailedEvents, ...action.payload.results],
        totalEvents: action.payload.count,
        hasMore: !!action.payload.next,
      };
    }
    case Action.DELETE_EVENT_SUCCESS: {
      const updatedEvents = state.detailedEvents.filter((de: IDetailedEvent) => de.id != action.payload.id);
      return {
        ...state,
        totalEvents: state.detailedEvents != updatedEvents ? state.totalEvents - 1 : state.totalEvents,
        detailedEvents: updatedEvents,
      };
    }
    case Action.GET_CONTRIBUTOR_SUCCESS: {
      return {
        ...state,
        contributorInfo: action.payload,
      };
    }
    case Action.GET_SINGLE_EVENT_SUCCESS: {
      return {
        ...state,
        singleEvent: action.payload,
      };
    }
    case Action.GET_ONE_EVENT_SUCCESS: {
      return {
        ...state,
        oneEventForEdit: action.payload,
      };
    }
    case Action.SET_EVENT_STATUS_SUCCESS: {
      return {
        ...state,
        oneEventForEdit: action.payload,
        isProcessingEvent: false,
      };
    }
    case Action.EVENT_DETAIL_STATUS_UPDATE_SUCCESS: {
      return {
        ...state,
        oneEventForEdit: action.payload,
        isProcessingEvent: false,
      };
    }
    case Action.CLEAR_EVENTS: {
      return {
        ...state,
        eventsList: [],
      };
    }
    case Action.CLEAR_CONTRIBUTOR: {
      return {
        ...state,
        contributorInfo: null,
      };
    }
    case Action.CLEAR_SINGLE_EVENT: {
      return {
        ...state,
        singleEvent: null,
      };
    }
    case Action.GET_USER_DATA_SUCCESS: {
      return {
        ...state,
        userData: action.payload,
        favorites: action.payload.favorites,
      };
    }
    case Action.POST_PHONETIC_SUCCESS: {
      return {
        ...state,
        phonetics: [...state.phonetics.filter((p) => p.id !== action.payload.id), action.payload],
      };
    }
    case Action.LIST_PHONETICS_SUCCESS: {
      return {
        ...state,
        phonetics: action.payload,
      };
    }
    case Action.DELETE_PHONETIC_SUCCESS: {
      return {
        ...state,
        phonetics: state.phonetics.filter((phonetic) => phonetic.id !== action.payload),
      };
    }
    case Action.UPDATE_EVENT:
    case Action.PUT_EVENT_ONLINE:
    case Action.PUT_EVENT_DETAIL_ONLINE:
    case Action.PUT_EVENT_OFFLINE:
    case Action.PUT_EVENT_DETAIL_OFFLINE:
    case Action.REJECT_EVENT:
    case Action.REJECT_EVENT_DETAIL:
    case Action.CREATE_EVENT: {
      return {
        ...state,
        isProcessingEvent: true,
      };
    }

    case Action.CREATE_EVENT_FAILURE:
    case Action.UPDATE_EVENT_FAILURE:
    case Action.PUT_EVENT_ONLINE_SUCCESS:
    case Action.PUT_EVENT_DETAIL_ONLINE_SUCCESS:
    case Action.PUT_EVENT_OFFLINE_SUCCESS:
    case Action.PUT_EVENT_DETAIL_OFFLINE_SUCCESS:
    case Action.REJECT_EVENT_SUCCESS:
    case Action.REJECT_EVENT_DETAIL_SUCCESS: {
      return {
        ...state,
        isProcessingEvent: false,
      };
    }
    case Action.PUT_EVENT_ONLINE_FAILURE:
    case Action.PUT_EVENT_DETAIL_ONLINE_FAILURE:
    case Action.PUT_EVENT_OFFLINE_FAILURE:
    case Action.PUT_EVENT_DETAIL_OFFLINE_FAILURE:
    case Action.REJECT_EVENT_FAILURE:
    case Action.REJECT_EVENT_DETAIL_FAILURE: {
      return {
        ...state,
        isProcessingEvent: false,
      };
    }
    case Action.UPDATE_EVENT_SUCCESS:
    case Action.CREATE_EVENT_SUCCESS: {
      return {
        ...state,
        eventPrices: [],
        isProcessingEvent: false,
      };
    }

    case Action.SET_EVENT_PRICES:
    case Action.GENERATE_EVENT_PRICES_SUCCESS: {
      return {
        ...state,
        eventPrices: action.payload,
      };
    }
    case Action.GENERATE_AI_PODCAST: {
      return {
        ...state,
        loadingPodcast: true,
      };
    }
    case Action.GENERATE_AI_PODCAST_SUCCESS: {
      return {
        ...state,
        loadingPodcast: false,
        podcast: action.payload,
      };
    }
    case Action.GENERATE_AI_TRANSCRIPT: {
      return {
        ...state,
        loadingTranscript: true,
      };
    }
    case Action.GENERATE_AI_TRANSCRIPT_SUCCESS: {
      return {
        ...state,
        loadingTranscript: false,
        transcript: action.payload.transcript,
      };
    }
    case Action.CLEAR_AI_TRANSCRIPT: {
      return {
        ...state,
        loadingTranscript: false,
        transcript: '',
      };
    }
    default:
      return state;
  }
};

export const eventsReducer = withLoadable([
  Action.GET_EVENTS,
  Action.GET_DETAILED_EVENTS,
  Action.GET_CONTRIBUTOR,
  Action.GET_SINGLE_EVENT,
  Action.GET_ONE_EVENT,
  Action.SET_EVENT_STATUS,
  Action.GET_CONTRIBUTOR_EVENTS,
  Action.GET_USER_DATA,
  Action.POST_COMMENT,
  Action.PATCH_REPORT,
  Action.GET_FAVORITES,
  Action.POST_PHONETIC,
  Action.DELETE_PHONETIC,
  Action.LIST_PHONETICS,
  Action.DELETE_EVENT,
  Action.UPDATE_EVENT,
  Action.CREATE_EVENT,
  Action.PUT_EVENT_DETAIL_ONLINE,
  Action.PUT_EVENT_DETAIL_OFFLINE,
  Action.REJECT_EVENT_DETAIL,
])(reducer);
